/**
 * Admin API client for users, organisations, and licenses
 */

import { apiClient } from './api'
import type {
  User,
  Organisation,
  License,
  CreateUserRequest,
  UpdateUserRequest,
  CreateOrganisationRequest,
  UpdateOrganisationRequest,
  CreateLicenseRequest,
  UpdateLicenseRequest,
  ExtendLicenseRequest,
  PaginatedResponse,
  ListUsersParams,
  ListOrganisationsParams,
  ListLicensesParams,
  ValidateXeroOrgsRequest,
  ValidateXeroOrgsResponse,
  UserSeatStatus,
  SupportCodeLookup,
} from '../types'

/**
 * A one-time password issued by the server. Shown to the operator once; only its hash is stored,
 * so it cannot be retrieved again.
 */
export interface IssuedPassword {
  password: string
}

/** A newly created user together with their one-time password. */
export interface CreatedUser {
  user: User
  issuedPassword: string
}

function buildQueryString(params: Record<string, string | number | undefined>): string {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') searchParams.set(key, String(value))
  }
  const query = searchParams.toString()
  return query ? `?${query}` : ''
}

// =============================================================================
// User Management
// =============================================================================

/**
 * List users with pagination and optional case-insensitive substring search
 * (FR3: matches email, username, displayName, organisation name on the
 * backend). Empty `search` is omitted from the query string so the backend
 * sees no `search` parameter at all.
 */
export async function listUsers(params?: ListUsersParams): Promise<PaginatedResponse<User>> {
  const query = buildQueryString({
    limit: params?.limit,
    offset: params?.offset,
    organisationId: params?.organisationId,
    search: params?.search,
  })
  return apiClient.get<PaginatedResponse<User>>(`/admin/users${query}`)
}

/**
 * Get a user by ID
 */
export async function getUser(id: string): Promise<User> {
  return apiClient.get<User>(`/admin/users/${id}`)
}

/**
 * Create a new user
 */
export async function createUser(data: CreateUserRequest): Promise<CreatedUser> {
  return apiClient.post<CreatedUser>('/admin/users', data)
}

/**
 * Update a user
 */
export async function updateUser(id: string, data: UpdateUserRequest): Promise<User> {
  return apiClient.put<User>(`/admin/users/${id}`, data)
}

/**
 * Delete a user
 */
export async function deleteUser(id: string): Promise<void> {
  return apiClient.delete(`/admin/users/${id}`)
}

/**
 * Reset a user's password to a freshly generated one-time password.
 *
 * Returns the issued password. It is shown to the operator once and is not recoverable
 * afterwards — only its hash is stored server-side.
 *
 * Admin operation - sets password_must_change flag so user must
 * change password on next login.
 *
 * @param id - User ID to reset password for
 */
export async function resetUserPassword(id: string): Promise<IssuedPassword> {
  return apiClient.post<IssuedPassword>(`/admin/users/${id}/reset-password`)
}

// =============================================================================
// Organisation Management
// =============================================================================

/**
 * List organisations with pagination
 * Supports optional filtering by xeroOrgId for exact match lookup
 */
export async function listOrganisations(
  params?: ListOrganisationsParams
): Promise<PaginatedResponse<Organisation>> {
  const query = buildQueryString({
    limit: params?.limit,
    offset: params?.offset,
    orgType: params?.orgType,
    status: params?.status,
    xeroOrgId: params?.xeroOrgId,
  })
  return apiClient.get<PaginatedResponse<Organisation>>(`/admin/organisations${query}`)
}

/**
 * Get an organisation by ID
 */
export async function getOrganisation(id: string): Promise<Organisation> {
  return apiClient.get<Organisation>(`/admin/organisations/${id}`)
}

/**
 * Create a new organisation
 */
export async function createOrganisation(data: CreateOrganisationRequest): Promise<Organisation> {
  return apiClient.post<Organisation>('/admin/organisations', data)
}

/**
 * Update an organisation
 */
export async function updateOrganisation(
  id: string,
  data: UpdateOrganisationRequest
): Promise<Organisation> {
  return apiClient.put<Organisation>(`/admin/organisations/${id}`, data)
}

/**
 * Delete an organisation
 */
export async function deleteOrganisation(id: string): Promise<void> {
  return apiClient.delete(`/admin/organisations/${id}`)
}

/**
 * Validate Xero Organisation IDs for import
 * Pre-validates a list of Xero Organisation IDs to check if they are linked
 * to existing EvidenX organisations before allowing an import.
 */
export async function validateXeroOrganisations(
  xeroOrganisationIds: string[]
): Promise<ValidateXeroOrgsResponse> {
  const request: ValidateXeroOrgsRequest = { xeroOrganisationIds }
  return apiClient.post<ValidateXeroOrgsResponse>('/admin/organisations/validate-xero', request)
}

// =============================================================================
// License Management
// =============================================================================

/**
 * List licenses with pagination
 */
export async function listLicenses(
  params?: ListLicensesParams
): Promise<PaginatedResponse<License>> {
  const query = buildQueryString({
    limit: params?.limit,
    offset: params?.offset,
    organisationId: params?.organisationId,
    licenseType: params?.licenseType,
    status: params?.status,
  })
  return apiClient.get<PaginatedResponse<License>>(`/admin/licenses${query}`)
}

/**
 * Get a license by ID
 */
export async function getLicense(id: string): Promise<License> {
  return apiClient.get<License>(`/admin/licenses/${id}`)
}

/**
 * Create a new license
 */
export async function createLicense(data: CreateLicenseRequest): Promise<License> {
  return apiClient.post<License>('/admin/licenses', data)
}

/**
 * Update a license
 */
export async function updateLicense(id: string, data: UpdateLicenseRequest): Promise<License> {
  return apiClient.put<License>(`/admin/licenses/${id}`, data)
}

/**
 * Activate a license
 */
export async function activateLicense(id: string): Promise<License> {
  return apiClient.post<License>(`/admin/licenses/${id}/activate`)
}

/**
 * Suspend a license
 */
export async function suspendLicense(id: string): Promise<License> {
  return apiClient.post<License>(`/admin/licenses/${id}/suspend`)
}

/**
 * Extend a license validity
 */
export async function extendLicense(id: string, data: ExtendLicenseRequest): Promise<License> {
  return apiClient.post<License>(`/admin/licenses/${id}/extend`, data)
}

// =============================================================================
// Seat Assignment Management
// =============================================================================

export interface SeatAssignment {
  id: string
  licenseId: string
  userId: string | null
  organisationId: string
  assignedEmail: string
  featuresGranted: string[]
  dataVolumeBytes: number | null
  isActive: boolean
  assignedAt: string
  assignedBy: string | null
  revokedAt: string | null
  revokedBy: string | null
  createdAt: string
  updatedAt: string
}

export interface SeatWithUser {
  id: string
  licenseId: string
  organisationId: string
  assignedEmail: string
  userId: string | null
  userEmail: string | null
  userName: string | null
  userStatus: string | null
  featuresGranted: string[]
  dataVolumeBytes: number | null
  /** Case limit for this seat (null = use default from license) */
  caseLimit: number | null
  isActive: boolean
  assignedAt: string
}

export interface LicenseSeatSummary {
  licenseId: string
  licenseKey: string
  organisationId: string
  organisationName: string
  seatsTotal: number
  seatsUsed: number
  seatsAvailable: number
  seats: SeatWithUser[]
}

export interface UserActivationStatus {
  userId: string
  email: string
  username: string
  status: string
  organisationId: string | null
  organisationName: string | null
  seatAssignmentId: string | null
  licenseId: string | null
  licenseKey: string | null
  licenseStatus: string | null
  licenseValid: boolean | null
  seatActive: boolean | null
  featuresGranted: string[] | null
  dataVolumeBytes: number | null
  activationEligibility: string
}

export interface ActivationEligibilityResponse {
  canActivate: boolean
  userId: string
  eligibility: string
  details: UserActivationStatus | null
}

export interface CreateSeatRequest {
  licenseId: string
  assignedEmail: string
  featuresGranted?: string[]
  dataVolumeBytes?: number
}

export interface CreateAndAssignRequest {
  licenseId: string
  userId: string
  featuresGranted?: string[]
  dataVolumeBytes?: number
}

export interface AssignUserRequest {
  userId: string
}

/**
 * List seats for a license
 */
export async function listSeatsForLicense(licenseId: string): Promise<SeatWithUser[]> {
  return apiClient.get<SeatWithUser[]>(`/admin/seats/license/${licenseId}`)
}

/**
 * Get seat summary for a license
 */
export async function getSeatSummary(licenseId: string): Promise<LicenseSeatSummary> {
  return apiClient.get<LicenseSeatSummary>(`/admin/seats/license/${licenseId}/summary`)
}

/**
 * Get a seat by ID
 */
export async function getSeat(id: string): Promise<SeatAssignment> {
  return apiClient.get<SeatAssignment>(`/admin/seats/${id}`)
}

/**
 * Get user's seat assignment
 */
export async function getUserSeat(userId: string): Promise<SeatAssignment | null> {
  return apiClient.get<SeatAssignment | null>(`/admin/seats/user/${userId}`)
}

/**
 * Create a new seat assignment (reserve for email)
 */
export async function createSeat(data: CreateSeatRequest): Promise<SeatAssignment> {
  return apiClient.post<SeatAssignment>('/admin/seats', data)
}

/**
 * Create and assign seat to user
 */
export async function createAndAssignSeat(data: CreateAndAssignRequest): Promise<SeatAssignment> {
  return apiClient.post<SeatAssignment>('/admin/seats/assign', data)
}

/**
 * Assign user to existing seat
 */
export async function assignUserToSeat(
  seatId: string,
  data: AssignUserRequest
): Promise<SeatAssignment> {
  return apiClient.post<SeatAssignment>(`/admin/seats/${seatId}/assign`, data)
}

/**
 * Unassign user from seat
 */
export async function unassignUserFromSeat(seatId: string): Promise<SeatAssignment> {
  return apiClient.post<SeatAssignment>(`/admin/seats/${seatId}/unassign`)
}

/**
 * Revoke a seat assignment
 */
export async function revokeSeat(seatId: string): Promise<SeatAssignment> {
  return apiClient.post<SeatAssignment>(`/admin/seats/${seatId}/revoke`)
}

/**
 * Get user activation status
 */
export async function getActivationStatus(userId: string): Promise<ActivationEligibilityResponse> {
  return apiClient.get<ActivationEligibilityResponse>(`/admin/seats/activation/${userId}`)
}

/**
 * List activation status for org
 */
export async function listActivationStatusForOrg(orgId: string): Promise<UserActivationStatus[]> {
  return apiClient.get<UserActivationStatus[]>(`/admin/seats/activation/organisation/${orgId}`)
}

/**
 * Activate a user
 */
export async function activateUser(userId: string): Promise<void> {
  return apiClient.post(`/admin/seats/activation/${userId}/activate`)
}

/**
 * Deactivate a user
 */
export async function deactivateUser(userId: string): Promise<void> {
  return apiClient.post(`/admin/seats/activation/${userId}/deactivate`)
}

/**
 * Available license for seat assignment during reactivation
 */
export interface AvailableLicenseForSeat {
  licenseId: string
  licenseKey: string
  licenseType: string
  seatsTotal: number
  seatsUsed: number
  seatsAvailable: number
  validUntil: string | null
  featuresGranted: string[]
}

/**
 * Result of a reactivation attempt
 */
export interface ReactivateResult {
  success: boolean
  seatAssignmentId: string | null
  availableLicenses: AvailableLicenseForSeat[] | null
  message: string
}

/**
 * Reactivate a suspended user
 * Used to restore access to users who were manually suspended or suspended due to license expiry.
 *
 * When a user is suspended, their seat is revoked. To reactivate:
 * - If licenseId is provided, creates a seat on that license
 * - If not provided and exactly one license has available seats, uses that
 * - If not provided and multiple licenses have seats, returns them for selection
 */
export async function reactivateUser(
  userId: string,
  licenseId?: string
): Promise<ReactivateResult> {
  return apiClient.post<ReactivateResult>(`/admin/seats/activation/${userId}/reactivate`, {
    licenseId,
  })
}

/**
 * Get available licenses for reactivating a user
 * Returns licenses in the user's organisation that have available seats
 */
export async function getAvailableLicensesForUser(
  userId: string
): Promise<AvailableLicenseForSeat[]> {
  return apiClient.get<AvailableLicenseForSeat[]>(
    `/admin/seats/activation/${userId}/available-licenses`
  )
}

// =============================================================================
// User Seat Status & Support Codes
// =============================================================================

/**
 * Get detailed seat status for a user
 * Includes eligibility, seat info, license info, and support code if any
 */
export async function getUserSeatStatus(userId: string): Promise<UserSeatStatus> {
  return apiClient.get<UserSeatStatus>(`/admin/users/${userId}/seat-status`)
}

/**
 * Look up a support code
 * Returns user and organisation details for the code
 */
export async function lookupSupportCode(code: string): Promise<SupportCodeLookup> {
  return apiClient.get<SupportCodeLookup>(`/admin/support-codes/${code}`)
}

/**
 * Resolve a support code (mark as fixed)
 */
export async function resolveSupportCode(code: string): Promise<void> {
  return apiClient.post(`/admin/support-codes/${code}/resolve`)
}

/**
 * Cleanup expired support codes
 * Returns the number of codes deleted
 */
export async function cleanupSupportCodes(): Promise<{ deletedCount: number }> {
  return apiClient.post<{ deletedCount: number }>('/admin/support-codes/cleanup')
}

// =============================================================================
// Case Limit Management (Org Admin)
// =============================================================================

/**
 * Case limit info for a single seat
 */
export interface SeatCaseLimitInfo {
  seatId: string
  userId: string | null
  userEmail: string | null
  /** Explicit case limit set for this seat (null = use default) */
  caseLimit: number | null
  /** Default case limit calculated from license total / seat count */
  defaultCaseLimit: number
  /** Effective case limit (explicit or default) */
  effectiveCaseLimit: number
}

/**
 * Summary of case limit allocation for an organisation's license
 */
export interface OrgCaseLimitSummary {
  licenseId: string
  /** Total case limit from the license (null = unlimited) */
  caseLimitTotal: number | null
  /** Sum of explicitly allocated case limits */
  caseLimitAllocated: number
  /** Remaining case limit available for allocation (null if unlimited) */
  caseLimitAvailable: number | null
  /** Number of seats on this license */
  seatCount: number
  /** Default case limit per seat (total / seat count, or 0 if unlimited) */
  defaultCaseLimitPerSeat: number
  /** Individual seat case limit info */
  seats: SeatCaseLimitInfo[]
}

/**
 * Request to update a seat's case limit
 */
export interface UpdateSeatCaseLimitRequest {
  /** New case limit (null to use default from license) */
  caseLimit: number | null
}

/**
 * Get case limit summary for a license
 * Returns allocation overview and per-seat limits
 */
export async function getCaseLimitSummary(licenseId: string): Promise<OrgCaseLimitSummary> {
  return apiClient.get<OrgCaseLimitSummary>(`/org/case-limits/${licenseId}`)
}

/**
 * Update case limit for a specific seat
 * Org admins can redistribute case allocations across seats
 */
export async function updateSeatCaseLimit(
  seatId: string,
  data: UpdateSeatCaseLimitRequest
): Promise<void> {
  return apiClient.patch(`/org/case-limits/seats/${seatId}`, data)
}

// =============================================================================
// Org Dashboard API
// =============================================================================

import type { OrgCasesSummary, OrgStorageSummary, OrgCasesListResponse } from '../types/dashboard'

/**
 * Get cases summary for org admin dashboard
 * Returns status breakdown, priority, data volume, and user count
 */
export async function getOrgCasesSummary(): Promise<OrgCasesSummary> {
  return apiClient.get<OrgCasesSummary>('/org/dashboard/cases')
}

/**
 * Get storage summary for org admin dashboard
 * Returns used vs quota, and per-case breakdown
 */
export async function getOrgStorageSummary(): Promise<OrgStorageSummary> {
  return apiClient.get<OrgStorageSummary>('/org/dashboard/storage')
}

/**
 * List all org cases grouped by owner
 * For org admin cases management page
 */
export async function listOrgCases(): Promise<OrgCasesListResponse> {
  return apiClient.get<OrgCasesListResponse>('/org/cases')
}
